import React, { useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { AppState } from '../../stores/redux-store';
import { initiateUser as initiateUserConnect } from '../../services/user';
import { setAppContext } from '../../utilities/setAppContext';
import { setApplicationError as setApplicationErrorConnect } from '../../stores/app-state-store';
import { useAnalytics } from '../../core/Analytics/AnalyticsProvider';
import { useVersion } from '../ApplicationProviders/VersionProvider/VersionProvider';
import { useProduct } from '../ApplicationProviders/ProductProvider/ProductProvider';
import { applicationParameters } from '../../utilities/application-parameters';

export type UserInitiatorProps = PropsFromRedux;

export const UserInitiatorPresenter: React.FC<UserInitiatorProps> = ({
  user,
  children,
  initiateUser,
  setApplicationError,
}) => {
  const {
    version: { version },
  } = useVersion();
  const { productType, productDimension } = useProduct();
  const { tinklinkAnalytics } = useAnalytics();
  const { parameters, parametersValidationError } = useMemo(
    () =>
      setAppContext({
        version,
        productType,
        productDimension,
        validateRedirectUrlParameters: false,
      }),
    [version, productType, productDimension]
  );

  useEffect(() => {
    tinklinkAnalytics.initialize({ appParameters: parameters, version, productType });
  }, [parameters, tinklinkAnalytics, version, productType]);

  useEffect(() => {
    if (!user && !parametersValidationError) {
      const clientId = applicationParameters.get('clientId');
      const authorizationHeader = applicationParameters.get('authorizationToken');
      const authorizationCode = applicationParameters.get('authorizationCode');
      const handoffSessionId = applicationParameters.get('handoffSessionId');

      initiateUser({ clientId, authorizationHeader, authorizationCode, handoffSessionId });
    }
  }, [initiateUser, parametersValidationError, user]);

  useEffect(() => {
    if (parametersValidationError) {
      setApplicationError(parametersValidationError);
    }
  }, [parametersValidationError, setApplicationError]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const mapStateToProps = (state: AppState) => {
  return {
    user: state.user.user,
  };
};

const mapDispatchToProps = {
  initiateUser: initiateUserConnect,
  setApplicationError: setApplicationErrorConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const UserInitiator = connector(UserInitiatorPresenter);
